import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormsModule,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { ShortcutsService } from 'app/layout/common/shortcuts/shortcuts.service';
import { Shortcut } from 'app/layout/common/shortcuts/shortcuts.types';
import { ICON_CATEGORIES, IconCategory } from 'app/layout/common/shortcuts/shortcuts-icon-data';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'shortcuts',
    templateUrl: './shortcuts.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'shortcuts',
    imports: [
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        NgClass,
        NgTemplateOutlet,
        RouterLink,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSlideToggleModule,
        TranslocoDirective,
    ],
})
export class ShortcutsComponent implements OnInit, OnDestroy {
    @ViewChild('shortcutsOrigin') private _shortcutsOrigin: MatButton;
    @ViewChild('shortcutsPanel') private _shortcutsPanel: TemplateRef<any>;

    mode: 'view' | 'modify' | 'add' | 'edit' | 'pick-icon' = 'view';
    shortcutForm: UntypedFormGroup;
    shortcuts: Shortcut[];
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /** Icon picker state */
    iconCategories: IconCategory[] = ICON_CATEGORIES;
    activeCategory: IconCategory = ICON_CATEGORIES[0];
    iconSearch = '';
    private _previousMode: 'add' | 'edit' = 'add';

    /** Max icons to render before search filter (performance) */
    private readonly _maxDisplayIcons = 200;

    get displayedIcons(): string[] {
        const icons = this.activeCategory.icons;
        if (this.iconSearch) {
            const search = this.iconSearch.toLowerCase();
            return icons.filter(name => name.includes(search)).slice(0, this._maxDisplayIcons);
        }
        return icons.slice(0, this._maxDisplayIcons);
    }

    get totalMatchCount(): number {
        if (!this.iconSearch) {
            return this.activeCategory.icons.length;
        }
        const search = this.iconSearch.toLowerCase();
        return this.activeCategory.icons.filter(name => name.includes(search)).length;
    }

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _formBuilder: UntypedFormBuilder,
        private _shortcutsService: ShortcutsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void {
        this.shortcutForm = this._formBuilder.group({
            id: [null],
            label: ['', Validators.required],
            description: [''],
            icon: ['', Validators.required],
            link: ['', Validators.required],
            useRouter: ['', Validators.required],
        });

        this._shortcutsService.shortcuts$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((shortcuts: Shortcut[]) => {
                this.shortcuts = shortcuts;
                this._changeDetectorRef.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    openPanel(): void {
        if (!this._shortcutsPanel || !this._shortcutsOrigin) {
            return;
        }
        this.mode = 'view';
        if (!this._overlayRef) {
            this._createOverlay();
        }
        this._overlayRef.attach(
            new TemplatePortal(this._shortcutsPanel, this._viewContainerRef)
        );
    }

    closePanel(): void {
        this._overlayRef.detach();
    }

    changeMode(mode: 'view' | 'modify' | 'add' | 'edit'): void {
        this.mode = mode;
    }

    newShortcut(): void {
        this.shortcutForm.reset();
        this.mode = 'add';
    }

    editShortcut(shortcut: Shortcut): void {
        this.shortcutForm.reset(shortcut);
        this.mode = 'edit';
    }

    /** Open the full icon picker panel */
    openIconPicker(): void {
        this._previousMode = this.mode as 'add' | 'edit';
        this.iconSearch = '';
        this.activeCategory = ICON_CATEGORIES[0];
        this.mode = 'pick-icon';
    }

    /** Select icon category tab */
    setIconCategory(category: IconCategory): void {
        this.activeCategory = category;
        this.iconSearch = '';
    }

    /** Pick an icon and return to form */
    selectIcon(iconName: string): void {
        const fullIcon = `${this.activeCategory.namespace}:${iconName}`;
        this.shortcutForm.patchValue({ icon: fullIcon });
        this.mode = this._previousMode;
    }

    /** Cancel icon picker, return to form */
    cancelIconPicker(): void {
        this.mode = this._previousMode;
    }

    save(): void {
        const shortcut = this.shortcutForm.value;
        if (shortcut.id) {
            this._shortcutsService.update(shortcut.id, shortcut).subscribe();
        } else {
            this._shortcutsService.create(shortcut).subscribe();
        }
        this.mode = 'modify';
    }

    delete(): void {
        const shortcut = this.shortcutForm.value;
        this._shortcutsService.delete(shortcut.id).subscribe();
        this.mode = 'modify';
    }

    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private _createOverlay(): void {
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(
                    this._shortcutsOrigin._elementRef.nativeElement
                )
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }
}
